<template>
  <div class="news-page">
    <div class="container">
      <!-- Top Hero Article -->
      <section v-if="heroArticle" class="news-hero">
        <div class="hero-image-wrap">
          <img
            :src="heroArticle.pic"
            class="hero-image"
          />
        </div>
        <div class="hero-content">
          <h1 class="hero-title">{{ heroArticle.title }}</h1>
          <div class="hero-date">{{ heroArticle.publishTime }}</div>
          <p class="hero-desc">{{ heroArticle.subTitle }}</p>
        </div>
      </section>

      <!-- Article Grid -->
      <section class="news-grid">
        <div v-for="item in newsList" :key="item.id" class="news-card">
          <div class="card-image-wrap">
            <img :src="item.pic"  class="card-image" />
          </div>
          <div class="card-content">
            <h2 class="card-title">{{ item.title }}</h2>
            <div class="card-date">{{ item.publishTime }}</div>
            <p class="card-desc">{{ item.subTitle }}</p>
          </div>
        </div>
      </section>
    </div>
    <AppFooter />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import AppFooter from "@/components/AppFooter.vue";

const router = useRouter();
const api = useApi();

interface NewsItem {
  id: number;
  title: string;
  publishTime: string;
  subTitle: string;
  pic: string;
}

const heroArticle = ref<NewsItem | null>(null);
const newsList = ref<NewsItem[]>([]);

const placeholderImg = "";

const mapNews = (x: any): NewsItem => {
  const rawDesc = x?.content ?? x?.desc ?? x?.intro ?? "";
  const cleanDesc =
    String(rawDesc || "")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, "")
      .trim() || "暂无描述";
  return {
    id: x?.id ?? x?.noticeId ?? x?.newsId ?? 0,
    title: x?.title,
    publishTime: x.publishTime,
    subTitle: x.subTitle,
    pic: x?.pic ?? x?.cover ?? placeholderImg,
  };
};

const fetchNews = async () => {
  const r = await api.get<any>(
    "/shop/notice/noticeList",
    { type: 1 },
    { loading: true, key: "news-notice-type-1" }
  );
  const data = (r as any)?.data ?? (r as any)?.raw ?? {};
  const arr = Array.isArray((data as any)?.records)
    ? (data as any).records
    : [];
  const mapped = arr.map(mapNews).filter((it: NewsItem) => !!it.title);
  heroArticle.value = mapped[0] || null;
  newsList.value = mapped.slice(1);
};

await fetchNews();
</script>

<style scoped>
.news-page {
  background-color: #fff;
  min-height: 100vh;
  padding-top: 60px; /* Space for fixed header if needed */
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Hero Section */
.news-hero {
  margin-bottom: 80px;
  cursor: pointer;
}

.hero-image-wrap {
  width: 100%;
  height: 500px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.news-hero:hover .hero-image {
  transform: scale(1.02);
}

.hero-content {
  text-align: center;
  width: 100%;
  margin: 0 auto;
  padding: 40px;
  background-color: #f9f9f9; /* Light gray background for text area in hero? Mockup shows text area is light gray or white? Actually mockup shows text below image on a light gray box? 
     Wait, looking at mockup:
     Top Image is full width of container.
     Below it is a light gray box containing Title, Date, Desc.
  */
  background-color: #f8f8f8;
}

.hero-title {
  font-size: 28px;
  color: #333;
  margin-bottom: 16px;
  font-weight: normal;
}

.hero-date {
  font-size: 14px;
  color: #999;
  margin-bottom: 24px;
}

.hero-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  max-width: 600px;
  margin: 0 auto;
}

/* Grid Section */
.news-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
}

.news-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.card-image-wrap {
  width: 100%;
  height: 300px;
  overflow: hidden;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.news-card:hover .card-image {
  transform: scale(1.02);
}

.card-content {
  background-color: #f8f8f8;
  padding: 40px;
  text-align: center;
  flex: 1;
}

.card-title {
  font-size: 24px;
  color: #333;
  margin-bottom: 12px;
  font-weight: normal;
}

.card-date {
  font-size: 13px;
  color: #999;
  margin-bottom: 20px;
}

.card-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.8;
}

@media (max-width: 768px) {
  .news-grid {
    grid-template-columns: 1fr;
  }

  .hero-image-wrap {
    height: 300px;
  }
}
</style>
